// infrastructure/mappers/providerMapper.js
import { createNpi, npiValue } from '../../domain/model/Npi';

/**
 * The boundary between a person's stored document and the Provider this context reads it as.
 *
 * TWO DIRECTIONS, WHICH IS UNUSUAL HERE AND IS THE POINT. Most mappers in this context read a
 * collection this context owns. This one reads a document ANOTHER area owns, and hands a patch back
 * to whoever is editing it. Both halves are deliberate:
 *
 * IN — the stored record is a person, in the platform's vocabulary: a `clients` document carrying
 * `type: 'staff'`, a `clientName` and a `clinic`. The domain wants a Provider with a `providerId`,
 * a `clinicId` and an `Npi`. This file is where those two spellings meet, so the platform's naming
 * stays in one place instead of spreading through the context.
 *
 * OUT — and NOT as a write. The NPI sits on the very document the staff editor is already saving, so
 * a second write path would be a second writer against a record being edited, and the outer save
 * would put its own stale copy back over the top. So this produces a PATCH in the document's own
 * vocabulary, the host merges it into the draft it already holds, and there stays exactly one
 * writer. The same reasoning the coverage panel records for the client fields it can repair.
 *
 * THE PATCH IS DELIBERATELY NARROW. It names the one field this context owns and nothing else,
 * because that document has several owners and a shape built for one of them would carry that one's
 * idea of the record into a save that was only meant to change an identifier.
 *
 * A MALFORMED STORED NPI READS AS ABSENT rather than throwing. It names no provider either way, and
 * an item that cannot name one is held with a reason rather than submitted with a guess. The editor
 * shows the field empty, which is the visible version of the same fact.
 */

/**
 * Reads a stored person document as a Provider.
 *
 * @param {Object|null|undefined} doc - a `clients` document with `type: 'staff'`
 * @returns {import('../../domain/model/Provider.types').Provider|null} null when there is no document
 */
export function toProvider(doc) {
  if (doc === null || doc === undefined) return null;

  return {
    providerId: doc.id ?? null,
    clinicId: doc.clinic ?? null,
    displayName: doc.clientName ?? null,
    npi: createNpi(doc.npi),
  };
}

/**
 * The patch the host merges into the draft it is already holding.
 *
 * Clearing is expressed as `null` rather than an omitted key, because an omitted key would leave
 * whatever is stored in place and the reader would see their deletion silently ignored.
 *
 * @param {import('../../domain/model/Npi').Npi|null|undefined} npi
 * @returns {{npi: string|null}}
 */
export function toProviderPatch(npi) {
  return { npi: npiValue(npi) };
}
